import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, FileCheck, Calendar, DollarSign, Shield, Clock, Heart, Award, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

const STEPS = [
  { icon: UserPlus, title: "Create your profile", text: "Choose Independent (sole trader) or Employed (PAYG). Upload your photo, services, hourly rate and short bio.", color: "bg-sky-100 text-sky-600" },
  { icon: FileCheck, title: "Get verified", text: "Upload NDIS Worker Screening, Police Check, First Aid, ID and Public Liability. Our team reviews each within 1–2 business days.", color: "bg-amber-100 text-amber-600" },
  { icon: Calendar, title: "Receive shift offers", text: "Verified workers see shifts in their suburb instantly. Accept the ones that fit your schedule.", color: "bg-emerald-100 text-emerald-600" },
  { icon: DollarSign, title: "Submit timesheets, get paid", text: "Submit hours after a shift. Once approved, we auto-generate an NDIS-aligned invoice and process payment via the participant's plan." , color: "bg-violet-100 text-violet-600"}
];

const PERKS = [
  { icon: DollarSign, title: "Set your own rate", text: "Earn $50–$120/hr depending on services. Independent workers keep more — we charge a flat platform fee, no opaque markups." },
  { icon: Clock, title: "Work when you want", text: "You choose the shifts and clients you take on. Switch to 'unavailable' anytime — no penalties." },
  { icon: Shield, title: "Insurance & support", text: "Public Liability cover for independent workers. Australian-based admin team who actually pick up the phone." },
  { icon: Award, title: "Build your reputation", text: "Every great shift earns reviews. Top-rated workers get prioritised in match suggestions and earn more." }
];

export default function HowItWorksWorker() {
  return (
    <div className="min-h-screen bg-white">
      <header className="container mx-auto px-4 py-5 flex items-center justify-between max-w-6xl">
        <Link to="/" className="font-bold text-xl text-slate-900">BTR Health</Link>
        <div className="flex gap-3">
          <Link to="/how-it-works/participants" className="text-sm text-slate-600 hover:text-slate-900 hidden sm:block self-center">For participants</Link>
          <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
          <Link to="/worker-signup"><Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full">Become a worker</Button></Link>
        </div>
      </header>

      <section className="container mx-auto px-4 py-16 max-w-4xl text-center">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
          Build a career<br /><span className="text-[#87CEEB]">on your terms</span>
        </motion.h1>
        <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Whether you want full-time hours or weekend shifts, BTR Health connects you with NDIS participants in your area — fast.
        </p>
      </section>

      <section className="container mx-auto px-4 max-w-5xl mb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center`}>
                  <s.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-semibold text-slate-400">STEP {i + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{s.title}</h3>
              <p className="text-slate-600 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">What you get with BTR Health</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PERKS.map(p => (
              <div key={p.title} className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-[#87CEEB]/10 flex items-center justify-center mb-3">
                  <p.icon className="w-5 h-5 text-[#87CEEB]" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">{p.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 max-w-3xl text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Ready to start earning?</h2>
        <p className="text-slate-600 mb-8 text-lg">Sign up takes 5 minutes. Verification within 1-2 business days.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/worker-signup"><Button size="lg" className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full px-8 h-12" data-testid="hiw-worker-signup">Become a support worker<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          <Link to="/help"><Button size="lg" variant="outline" className="rounded-full px-8 h-12">Read the FAQ</Button></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
